'use client';

/**
 * HomepageV3 OfferingCard - local port of the Claude handoff `.tc` card.
 *
 * Normal cards preserve the source fixed head heights, disclosure footnote
 * link, CTA, progress, and mobile width rules. Sold-out cards use the existing
 * local recap variant. Relative logo paths resolve through the discover public
 * logo path (`/homepage-v3/logos`) unless callers provide a backend/CDN URL.
 */
import type { KeyboardEvent, MouseEvent, ReactNode, Ref } from 'react';
import { styled } from '@mui/material/styles';
import {
  type BadgeClass,
  type CardFigure,
  COMPANIES,
  type Company,
  type OfferingCardData,
} from 'components/HomepageV3/data/demoData';
import { COLORS, FONTS, RADII } from 'components/HomepageV3/theme/tokens';
import { isRegA, isRegCf, isRegD, regDDisclosure } from 'components/HomepageV3/utils/disclosures';
import CompanyLogo, { shouldUseLogoBasePath } from 'components/HomepageV3/components/CompanyLogo';
import { withSe3AssetPrefix } from 'components/HomepageV3/assetPrefix';
import { tombstoneIntermediary } from 'utils/offerings/tombstone';
import { useSe3QaSearch, withSe3QaOverride } from 'components/HomepageV3/utils/routes';
import { useAssignedHomepageTombstoneVariant } from 'components/HomepageV3/experiments/tombstoneExperiment';
import { formatOfferingAmount } from 'components/HomepageV3/utils/offeringFigures';

const PUBLIC_LOGO_BASE = withSe3AssetPrefix('/homepage-v3/logos') as string;
const PRIVATE_SERIES_SUFFIX_RE = /(?:\s*,?\s*a series of StartEngine Private LLC)+\s*$/i;

const compactDollarValue = (value: string): string => {
  if (!/^\$[\d,]+(?:\.\d+)?$/.test(value.trim())) return value;
  const amount = Number(value.replace(/[$,]/g, ''));
  return Number.isFinite(amount) && amount >= 1000 ? formatOfferingAmount(amount) : value;
};

export type OfferingCardVariant = 'light' | 'dark';

export interface SoldOutCardContent {
  /** Meta pill copy (e.g. "Closed · Fully subscribed"). Omitted = no pill. */
  meta?: string;
  figL?: { lbl: string; val: string };
  /** `public: true` colors the status value teal-ink (e.g. "Public"). */
  figR?: { lbl: string; val: string; public?: boolean };
}

export interface OfferingCardProps {
  /** Page-owned rollout value for legal-entity disclosure copy. */
  useCompanyLegalName?: boolean;
  /** Card data mirroring SECTIONS[].cards (open/live offerings). */
  card?: OfferingCardData;
  /** Optional explicit company override (otherwise resolved from `card.co`). */
  company?: Company;
  /** `light` = cream/white card; `dark` = low-minimum card on night bg. */
  variant?: OfferingCardVariant;
  /** Render a previously-fully-subscribed "Sold Out" card instead. */
  soldOut?: SoldOutCardContent;
  /**
   * Disclosure copy beneath the head. Sold-out variant: footnote line. Live
   * variant: overrides the auto reg-type disclosure (and suppresses the
   * "footnote 1" link) — used by non-offering cards (e.g. Vino Vest) that must
   * not render a regulatory line they don't have.
   */
  disclosure?: string;
  /** @deprecated Progress visibility is derived from regulation and card data. */
  hideProgress?: boolean;
  /** @deprecated Editorial tombstones never render industry subheads. */
  hideIndustrySubhead?: boolean;
  /**
   * Override the regulation subhead line under the card name. Offerings made
   * outside the US reg framework (e.g. a Canadian prospectus exemption) have no
   * "Reg D"/"Reg A+" tag to derive, so they supply their own series line.
   */
  seriesSubhead?: string;
  /** Override the CTA label (defaults to "View details"). */
  ctaLabel?: string;
  /** Live variant: clamp long descriptions to keep dense shelf cards scannable. */
  descriptionClampLines?: number;
  /** Fill the parent width (shelf/grid surfaces) instead of the fixed 296px. */
  fluid?: boolean;
  /** Rewrites relative logo paths; defaults to the discover public path. */
  logoBasePath?: string;
  onView?: (anchor: HTMLElement) => void;
  /**
   * Activation for the tombstone body itself. Defaults to `onView`, so shelves
   * whose CTA opens the offering are unchanged. Surfaces whose CTA does
   * something else (e.g. the Canada tombstone's "Book a Call") pass their own
   * navigation here so clicking the card still goes to the offering page.
   */
  onCardActivate?: (anchor: HTMLElement) => void;
  onTrack?: () => void;
  cardRef?: Ref<HTMLDivElement>;
  className?: string;
  'data-cy'?: string;
}

const RestrictedCard = styled('div', {
  shouldForwardProp: (prop) => prop !== 'fluid',
})<{ fluid?: boolean }>(({ fluid }) => ({
  position: 'relative',
  width: fluid ? '100%' : '296px',
  maxWidth: '100%',
  minHeight: '520px',
  flexShrink: 0,
  overflow: 'hidden',
  border: `1px solid ${COLORS.edge}`,
  borderTop: `2px solid ${COLORS.gold}`,
  borderRadius: '20px',
  background: COLORS.card,
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 1px 0 rgba(11,41,45,0.02)',
  transition: 'box-shadow 0.18s, transform 0.18s, border-color 0.18s',
  '&:hover': {
    boxShadow: '0 18px 40px rgba(11,41,45,0.12)',
    borderColor: '#DDD2BF',
    transform: 'translateY(-3px)',
  },
  '@media (max-width: 640px)': {
    width: fluid ? '100%' : '80vw',
    maxWidth: fluid ? '420px' : '320px',
  },
}));

const RestrictedFrostedHead = styled('div')({
  position: 'relative',
  height: '238px',
  flexShrink: 0,
  overflow: 'hidden',
  borderBottom: `1px solid ${COLORS.edge}`,
  background:
    'radial-gradient(circle at 78% 18%, rgba(1,209,184,0.14), transparent 38%), linear-gradient(145deg, #0B292D, #071C1F)',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(180deg, rgba(11,41,45,0.06), rgba(11,41,45,0.48))',
    backdropFilter: 'blur(1px)',
  },
});

const RestrictedBadge = styled('span')({
  position: 'absolute',
  top: '18px',
  left: '18px',
  zIndex: 2,
  padding: '6px 10px',
  border: '1px solid rgba(233,215,182,0.34)',
  borderRadius: '999px',
  background: 'rgba(255,252,246,0.1)',
  color: COLORS.cream,
  fontFamily: FONTS.sans,
  fontSize: '9px',
  fontWeight: 700,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
});

const RestrictedSkeleton = styled('div')({
  position: 'absolute',
  inset: '76px 26px 28px',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '15px',
  filter: 'blur(6px)',
  opacity: 0.74,
  '& span': {
    height: '18px',
    borderRadius: '999px',
    background: 'rgba(255,252,246,0.46)',
  },
  '& span:nth-of-type(1)': { width: '68%', height: '30px' },
  '& span:nth-of-type(2)': { width: '88%' },
  '& span:nth-of-type(3)': { width: '58%' },
});

const RestrictedBody = styled('div')({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: '28px 26px 24px',
  color: COLORS.ink,
  background: COLORS.card,
});

const RestrictedIcon = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '30px',
  height: '30px',
  marginBottom: '14px',
  border: '1px solid rgba(181,138,75,0.44)',
  borderRadius: '9px',
  color: COLORS.tealInk,
  background: 'rgba(181,138,75,0.08)',
  lineHeight: 1,
});

const RestrictedTitle = styled('strong')({
  fontFamily: FONTS.serif,
  fontSize: '27px',
  fontWeight: 400,
  lineHeight: 1.08,
  marginBottom: '8px',
});

const RestrictedHint = styled('span')({
  maxWidth: '230px',
  color: COLORS.ink2,
  fontFamily: FONTS.sans,
  fontSize: '14px',
  lineHeight: 1.55,
});

const RestrictedCta = styled('span')({
  marginTop: 'auto',
  paddingTop: '22px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  color: COLORS.tealInk,
  fontFamily: FONTS.sans,
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
});

const RestrictedLockIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M7.5 10V7.5a4.5 4.5 0 0 1 9 0V10M6 10h12v10H6V10Z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* Sold-out card primitive (matches source markup) */

function liveDisc(company: Company, useCompanyLegalName: boolean): string {
  const intermediary = tombstoneIntermediary(company, useCompanyLegalName);
  if (company.regA) {
    return `This is a Regulation A+ offering of common stock in ${company.name}, conducted via ${intermediary}. You are buying ${company.name} shares directly.`;
  }
  if (company.regcf) {
    return `This is a Regulation Crowdfunding offering of securities in ${company.name}, conducted via ${intermediary}.`;
  }
  return regDDisclosure(company.companyName);
}

function shouldShowDisclosureLink(company: Company): boolean {
  return Boolean(company.regA || !company.regcf);
}

function offeringHref(company: Company): string | null {
  if (company.href) return company.href;
  return null;
}

function metaVariant(meta?: string): 'momentum' | 'ira' | 'eligibility' | '' {
  const normalized = (meta ?? '').toLowerCase();
  if (normalized.includes('sold out') || normalized.includes('waitlist')) return 'momentum';
  if (normalized.includes('ira')) return 'ira';
  if (
    normalized.includes('qualified') ||
    normalized.includes('accredited') ||
    normalized.includes('open to all')
  ) {
    return 'eligibility';
  }
  return '';
}

function privateSeriesDisplay(series?: string): string {
  return (series ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(PRIVATE_SERIES_SUFFIX_RE, '')
    .replace(/^Series\s+Series\s+/i, 'Series ');
}

function regulationSubhead(company: Company, isCrowd: boolean): string {
  if (isCrowd) return privateSeriesDisplay(company.series);
  return company.regD || /\breg(?:ulation)?\s*d\b/i.test(company.reg) ? 'Reg D' : '';
}

function regulationIntermediary(company: Company, useCompanyLegalName: boolean): string {
  let regulation = '';
  if (company.regA || isRegA(company.reg)) regulation = 'Reg A+';
  else if (company.regcf || isRegCf(company.reg)) regulation = 'Reg CF';
  else if (company.regD || isRegD(company.reg)) regulation = 'Reg D';

  return regulation
    ? `${regulation} via ${tombstoneIntermediary(company, useCompanyLegalName)}`
    : '';
}

/**
 * Private (Reg D) card title = the offering's stated nickname as-is (Paige QA):
 * we do NOT blanket-prepend "Series", which produced inaccurate names like
 * "Series Omni Fund" for Funds. When the nickname itself starts with "Series"
 * (e.g. "Series Panthalassa"), that leading word is italicised (tc-pre) to match
 * the design; Funds ("Omni Fund", "The Disruptor's Fund 2") render as-is.
 */
function privateCardTitle(name: string, dark?: boolean): ReactNode {
  const raw = (name ?? '').trim();
  const match = raw.match(/^Series\s+(.+)$/i);
  if (!match) return raw;
  return (
    <>
      <TcPre className="tc-pre" dark={dark}>
        Series{' '}
      </TcPre>
      {match[1]}
    </>
  );
}

const badgeTone = (cls: BadgeClass) => {
  if (cls === 'bdg-teal') {
    return {
      color: COLORS.tealInk,
      border: '1px solid rgba(12,111,100,0.35)',
      background: 'rgba(1,209,184,0.07)',
    };
  }
  if (cls === 'bdg-wine') {
    return {
      color: COLORS.wine,
      border: '1px solid rgba(163,58,76,0.35)',
      background: 'rgba(163,58,76,0.06)',
    };
  }
  return {
    color: '#8A6A2E',
    border: '1px solid rgba(181,138,75,0.45)',
    background: 'rgba(181,138,75,0.07)',
  };
};

const LiveCard = styled('div', {
  shouldForwardProp: (prop) => prop !== 'dark' && prop !== 'fluid',
})<{ dark?: boolean; fluid?: boolean }>(({ dark, fluid }) => ({
  background: dark ? COLORS.night : COLORS.card,
  border: dark ? '1px solid rgba(233,215,182,0.18)' : `1px solid ${COLORS.edge}`,
  borderTop: `2px solid ${COLORS.gold}`,
  borderRadius: '20px',
  cursor: 'pointer',
  transition: 'box-shadow 0.18s, transform 0.18s, border-color 0.18s',
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
  position: 'relative',
  width: fluid ? '100%' : '296px',
  maxWidth: '100%',
  overflow: 'hidden',
  boxShadow: dark ? 'none' : '0 1px 0 rgba(11,41,45,0.02)',
  '&:hover': {
    boxShadow: '0 18px 40px rgba(11,41,45,0.12)',
    borderColor: dark ? 'rgba(233,215,182,0.3)' : '#DDD2BF',
    transform: 'translateY(-3px)',
  },
  '@media (max-width: 640px)': {
    width: fluid ? '100%' : '80vw',
    maxWidth: fluid ? '420px' : '320px',
  },
}));

const LiveBanner = styled('div')({
  width: '100%',
  height: '79px',
  overflow: 'hidden',
  background: COLORS.night,
  '& img, & [data-cy="offering-card-banner"]': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
});

/**
 * Tombstone head. Uses minHeight (not a fixed height) so short cards share an
 * equal-height baseline (Hannah) while the head still grows to fit the full Reg
 * D legal disclosure. A fixed height + overflow:hidden previously clipped the
 * last disclosure line at narrow widths, where the copy wraps to more lines than
 * on desktop — truncating required legal text. overflow stays visible so no
 * disclosure line is ever sliced off.
 */
const LiveHead = styled('div', {
  shouldForwardProp: (prop) => prop !== 'dark' && prop !== 'hasBanner',
})<{ dark?: boolean; hasBanner?: boolean }>(({ dark, hasBanner }) => ({
  padding: hasBanner ? '18px 24px 18px' : '24px 24px 18px',
  minHeight: hasBanner ? '200px' : '288px',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  textAlign: 'center',
  borderBottom: dark ? '1px solid rgba(233,215,182,0.14)' : `1px solid ${COLORS.edge}`,
  position: 'relative',
  overflow: 'visible',
  '@media (max-width: 640px)': {
    minHeight: hasBanner ? '176px' : '264px',
  },
}));

const LiveBadge = styled('span', {
  shouldForwardProp: (prop) => prop !== 'tone' && prop !== 'hasBanner',
})<{ tone: BadgeClass; hasBanner?: boolean }>(({ tone, hasBanner }) => ({
  position: 'absolute',
  top: hasBanner ? '14px' : '16px',
  right: hasBanner ? '14px' : '16px',
  zIndex: 2,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  fontFamily: FONTS.sans,
  fontSize: '9px',
  fontWeight: 600,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  padding: '4px 10px',
  borderRadius: RADII.pill,
  whiteSpace: 'nowrap',
  ...badgeTone(tone),
  '& .dot': {
    width: '4px',
    height: '4px',
    borderRadius: '50%',
    background: 'currentColor',
  },
  ...(hasBanner
    ? { background: 'rgba(252,249,245,0.95)', boxShadow: '0 2px 10px rgba(11,41,45,0.35)' }
    : null),
}));

const LogoMark = styled('div')({
  margin: '6px 0 16px',
});

const LiveName = styled('div', {
  shouldForwardProp: (prop) => prop !== 'dark',
})<{ dark?: boolean }>(({ dark }) => ({
  fontFamily: FONTS.serif,
  fontSize: '26px',
  fontWeight: 400,
  color: dark ? '#FFFFFF' : COLORS.ink,
  lineHeight: 1.05,
  letterSpacing: '-0.01em',
}));

const TcPre = styled('span', {
  shouldForwardProp: (prop) => prop !== 'dark',
})<{ dark?: boolean }>(({ dark }) => ({
  fontStyle: 'italic',
  fontWeight: 400,
  color: dark ? COLORS.onDark2 : COLORS.ink3,
}));

const LiveSeries = styled('div', {
  shouldForwardProp: (prop) => prop !== 'dark',
})<{ dark?: boolean }>(({ dark }) => ({
  fontFamily: FONTS.mono,
  fontSize: '11px',
  color: dark ? COLORS.onDark2 : COLORS.ink2,
  marginTop: '9px',
  letterSpacing: '0.03em',
}));

/**
 * Legal disclosure line. The full text must always be visible — never
 * line-clamped or clipped; LiveHead flexes to fit it, so overflow stays visible.
 */
const LiveDisc = styled('div', {
  shouldForwardProp: (prop) => prop !== 'dark',
})<{ dark?: boolean }>(({ dark }) => ({
  fontFamily: FONTS.sans,
  fontSize: '9.5px',
  color: dark ? COLORS.onDark3 : COLORS.ink3,
  lineHeight: 1.5,
  marginTop: '12px',
  textAlign: 'left',
  textWrap: 'pretty',
  opacity: 0.85,
  overflow: 'visible',
  '& a': {
    color: dark ? COLORS.onDark3 : COLORS.ink3,
    textDecoration: 'underline',
    textDecorationColor: dark ? 'rgba(233,215,182,0.25)' : COLORS.edge,
    textUnderlineOffset: '2px',
  },
  '& a:hover': {
    color: COLORS.gold,
    textDecorationColor: COLORS.gold,
  },
}));

const LiveBody = styled('div')({
  padding: '18px 24px 22px',
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
});

// Reserve the compact eligibility-badge area in every tombstone body. A
// PostHog-configured badge therefore never changes an individual card's
// height or shifts its description/statistics relative to adjacent cards.
const LiveMetaSlot = styled('div')({
  minHeight: '37px',
  display: 'flex',
  alignItems: 'flex-start',
});

const LiveMeta = styled('div', {
  shouldForwardProp: (prop) => prop !== 'kind' && prop !== 'dark',
})<{ kind?: 'momentum' | 'ira' | 'eligibility' | ''; dark?: boolean }>(({ kind, dark }) => ({
  alignSelf: 'flex-start',
  display: 'inline-flex',
  alignItems: 'center',
  // Match the compact header badges. Eligibility labels such as "Qualified
  // Purchasers Exclusive" previously used a larger body treatment, wrapped
  // onto two lines, and visually overflowed the tombstone body.
  gap: '6px',
  fontFamily: FONTS.sans,
  fontSize: '9px',
  fontWeight: 600,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: dark ? COLORS.onDark2 : COLORS.ink2,
  margin: 0,
  padding: '4px 10px',
  border: dark ? '1px solid rgba(233,215,182,0.24)' : `1px solid ${COLORS.edge}`,
  borderRadius: RADII.pill,
  background: dark ? 'rgba(255,255,255,0.04)' : '#fff',
  maxWidth: '100%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  '&::before': {
    content: '""',
    width: '5px',
    height: '5px',
    borderRadius: '50%',
    background: dark ? COLORS.onDark3 : COLORS.ink3,
    flexShrink: 0,
  },
  ...(kind === 'momentum'
    ? {
        color: '#8A6A2E',
        borderColor: 'rgba(181,138,75,0.4)',
        background: 'rgba(181,138,75,0.06)',
        '&::before': {
          content: '"✦"',
          width: 'auto',
          height: 'auto',
          borderRadius: 0,
          background: 'none',
          color: '#B58A4B',
          fontSize: '9px',
          lineHeight: 1,
        },
      }
    : null),
  ...(kind === 'ira'
    ? {
        color: COLORS.tealInk,
        borderColor: 'rgba(12,111,100,0.32)',
        background: 'rgba(1,209,184,0.06)',
        '&::before': { background: COLORS.tealInk },
      }
    : null),
  ...(kind === 'eligibility'
    ? {
        color: dark ? COLORS.onDark2 : COLORS.ink2,
        borderStyle: 'dashed',
        background: 'transparent',
      }
    : null),
}));

const LiveDesc = styled('div', {
  shouldForwardProp: (prop) => prop !== 'dark' && prop !== 'clampLines',
})<{ dark?: boolean; clampLines?: number }>(({ dark, clampLines }) => ({
  fontFamily: FONTS.sans,
  fontSize: '16.5px',
  color: dark ? COLORS.onDark2 : COLORS.ink2,
  lineHeight: 1.6,
  marginBottom: '16px',
  flexGrow: 1,
  textWrap: 'pretty',
  ...(clampLines
    ? {
        display: '-webkit-box',
        WebkitLineClamp: clampLines,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        flexGrow: 0,
        // Reserve a FIXED clampLines-line height (not just a max) so clamped
        // cards — the sold-out / closed rail — are uniform height regardless of
        // description length (Hannah QA: a 2-line desc must reserve the same
        // space as a clamped 7-line one). 16.5px * 1.6 line-height. Live cards
        // pass no clampLines, so they're unaffected.
        height: `calc(16.5px * 1.6 * ${clampLines})`,
      }
    : null),
}));

const LiveFigs = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  borderTop: `1px solid ${COLORS.edge}`,
  paddingTop: '16px',
  marginBottom: '12px',
  '& .tc-fig': {
    flex: '0 1 auto',
  },
  '& .tc-fig.right': {
    marginLeft: 'auto',
    textAlign: 'right',
  },
});

const LiveFigLabel = styled('div')({
  fontFamily: FONTS.sans,
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: COLORS.gold,
  marginBottom: '6px',
  whiteSpace: 'nowrap',
});

function liveFigColor(tone: CardFigure['cls'], dark?: boolean): string {
  if (tone === 'urg') return COLORS.wine;
  if (tone === 'amber') return '#9A6516';
  return dark ? '#fff' : COLORS.ink;
}

const LiveFigValue = styled('div', {
  shouldForwardProp: (prop) => prop !== 'tone' && prop !== 'dark',
})<{ tone?: 'ok' | 'urg' | 'amber'; dark?: boolean }>(({ tone, dark }) => ({
  fontFamily: FONTS.serif,
  fontSize: 'clamp(18px, 1.5vw, 22px)',
  fontWeight: 400,
  color: liveFigColor(tone, dark),
  lineHeight: 1,
  whiteSpace: 'nowrap',
}));

const LiveTrack = styled('div')({
  height: '4px',
  background: COLORS.edge,
  borderRadius: '90px',
  overflow: 'hidden',
  marginBottom: '18px',
});

// Cards without a meter reserve its footprint as invisible whitespace so the
// disclosure and CTA stay aligned across mixed public/private shelves.
const LiveTrackSpacer = styled('div')({
  height: '4px',
  marginBottom: '18px',
});

const LiveIntermediary = styled('div', {
  shouldForwardProp: (prop) => prop !== 'dark',
})<{ dark?: boolean }>(({ dark }) => ({
  color: dark ? COLORS.onDark3 : COLORS.ink3,
  fontFamily: FONTS.mono,
  fontSize: '10px',
  lineHeight: 1.4,
  marginBottom: '14px',
  textAlign: 'left',
}));

const LiveFill = styled('div', {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone?: 'ok' | 'urg' | 'amber' }>(({ tone }) => ({
  height: '100%',
  borderRadius: '90px',
  background: tone === 'urg' ? COLORS.wine : '#C2860F',
}));

const liveCtaStyles = (dark?: boolean) => ({
  width: '100%',
  display: 'block',
  boxSizing: 'border-box' as const,
  fontFamily: FONTS.sans,
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  textAlign: 'center' as const,
  textDecoration: 'none',
  color: dark ? COLORS.champagne : COLORS.night,
  background: 'transparent',
  border: dark ? '1px solid rgba(233,215,182,0.4)' : `1px solid ${COLORS.night}`,
  padding: '12px',
  borderRadius: '12px',
  cursor: 'pointer',
  transition: 'all 0.15s',
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  '&:hover': dark
    ? {
        background: COLORS.champagne,
        color: COLORS.night,
        borderColor: COLORS.champagne,
      }
    : {
        background: COLORS.night,
        color: COLORS.cream,
      },
});

const LiveCta = styled('button', {
  shouldForwardProp: (prop) => prop !== 'dark',
})<{ dark?: boolean }>(({ dark }) => liveCtaStyles(dark));

const LiveCtaLink = styled('a', {
  shouldForwardProp: (prop) => prop !== 'dark',
})<{ dark?: boolean }>(({ dark }) => liveCtaStyles(dark));

const ExperimentCard = styled('div', {
  shouldForwardProp: (prop) => prop !== 'fluid',
})<{ fluid?: boolean }>(({ fluid }) => ({
  width: fluid ? '100%' : '296px',
  maxWidth: '100%',
  flexShrink: 0,
  cursor: 'pointer',
  fontFamily: FONTS.sans,
  display: 'flex',
  flexDirection: 'column',
  '@media (max-width: 640px)': {
    width: fluid ? '100%' : '80vw',
    maxWidth: fluid ? '420px' : '320px',
  },
}));

const MarketplaceCard = styled(ExperimentCard)({
  // Variant B contains several nowrap/intrinsic-width children. Without an
  // explicit zero min-width, those children can force a 296px shelf item to
  // grow, producing uneven image sizes and a much taller row.
  minWidth: 0,
  overflow: 'hidden',
});

const ExperimentTile = styled('div')({
  width: '100%',
  aspectRatio: '16 / 9',
  borderRadius: '14px',
  overflow: 'hidden',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(145deg, ${COLORS.tealInk}, ${COLORS.night})`,
  '& > .experiment-logo': {
    width: '62%',
    height: '45%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '& > .experiment-logo img': {
    maxWidth: '100%',
    maxHeight: '100%',
    filter: 'brightness(0) invert(1)',
  },
  '& > .experiment-logo span': {
    color: '#fff',
    fontFamily: FONTS.sans,
    fontSize: 'clamp(22px, 3vw, 38px)',
    fontWeight: 600,
    maxWidth: '82%',
    textAlign: 'center',
    lineHeight: 1.05,
  },
  '& > [data-cy="offering-card-banner"]': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
});

const ExperimentDisclosure = styled('div')({
  position: 'absolute',
  inset: 'auto 0 0',
  padding: '40px 14px 12px',
  color: 'rgba(255,255,255,0.85)',
  background:
    'linear-gradient(to top, rgba(11,25,28,0.82) 0%, rgba(11,25,28,0.6) 45%, rgba(11,25,28,0.28) 75%, rgba(11,25,28,0))',
  fontFamily: FONTS.sans,
  fontSize: '10px',
  lineHeight: 1.32,
  textAlign: 'left',
  '& a': { color: 'inherit', textDecoration: 'underline' },
});

const ExperimentLogoImage = styled('img')({});
const ExperimentTileImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
});

const MarketplaceBody = styled('div')({
  padding: '16px 2px 0',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
});
const MarketplaceName = styled('div')({
  color: COLORS.ink,
  fontSize: '20px',
  lineHeight: 1.2,
  fontWeight: 700,
  letterSpacing: '-0.01em',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  minHeight: '48px',
});
const MarketplaceDesc = styled('div')({
  color: COLORS.ink2,
  fontSize: '14.5px',
  lineHeight: 1.4,
  marginTop: '2px',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  minHeight: '41px',
});
const MarketplaceDivider = styled('div')({
  height: '1px',
  background: COLORS.edge,
  margin: '16px 0',
});
const MarketplaceStats = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  minHeight: '40px',
  marginBottom: '16px',
});
const MarketplaceStat = styled('div')({
  flex: '1 1 0',
  minWidth: 0,
});
const MarketplaceStatValue = styled('div')({
  color: COLORS.tealInk,
  fontSize: '19px',
  lineHeight: 1,
  fontWeight: 700,
  letterSpacing: '-0.01em',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});
const MarketplaceStatLabel = styled('div')({
  color: COLORS.ink2,
  fontSize: '12.5px',
  marginTop: '4px',
});
const MarketplaceReg = styled('div')({
  display: 'inline-block',
  color: COLORS.ink2,
  background: '#F3EDE2',
  borderRadius: '8px',
  padding: '6px 12px',
  marginTop: 'auto',
  alignSelf: 'flex-start',
  fontSize: '12px',
});

const HybridCard = styled(ExperimentCard)({
  background: COLORS.card,
  border: `1px solid ${COLORS.edge}`,
  borderTop: `2px solid ${COLORS.gold}`,
  borderRadius: '20px',
  overflow: 'hidden',
  boxShadow: '0 12px 30px rgba(11,41,45,0.08)',
  '& .experiment-tile': { borderRadius: 0 },
});
const HybridBody = styled('div')({
  padding: '20px 20px 22px',
  display: 'flex',
  flexDirection: 'column',
});
const HybridName = styled('div')({
  fontFamily: FONTS.serif,
  color: COLORS.ink,
  fontSize: '23px',
  lineHeight: 1.08,
  height: '50px',
  marginBottom: '16px',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});
const HybridDesc = styled('div')({
  color: COLORS.ink2,
  fontSize: '16px',
  lineHeight: 1.55,
  minHeight: '124px',
});
const HybridReg = styled('div')({
  color: COLORS.ink3,
  fontSize: '11px',
  marginBottom: '16px',
});

const SoldOutCard = styled('div', {
  shouldForwardProp: (prop) => prop !== 'fluid',
})<{ fluid?: boolean }>(({ fluid }) => ({
  background: '#FBF7F0',
  border: `1px solid ${COLORS.edge}`,
  borderTop: '2px solid #B8AE9E',
  borderRadius: '20px',
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
  width: fluid ? '100%' : '296px',
  maxWidth: '100%',
  overflow: 'hidden',
  boxShadow: '0 1px 0 rgba(11,41,45,0.02)',
}));

const SoldOutHead = styled('div')({
  padding: '24px 24px 18px',
  // Fixed head height keeps all sold-out tombstones equal-height across the rail
  // (Hannah QA) — mirrors the live card's non-banner head lock. Sold-out cards
  // never have a banner, so a single height applies.
  height: '288px',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  textAlign: 'center',
  borderBottom: `1px solid ${COLORS.edge}`,
  position: 'relative',
  '@media (max-width: 640px)': {
    height: '264px',
  },
});

const SoldOutBadge = styled('span')({
  position: 'absolute',
  top: '16px',
  right: '16px',
  display: 'inline-flex',
  alignItems: 'center',
  fontFamily: FONTS.sans,
  fontSize: '9px',
  fontWeight: 600,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  padding: '4px 10px',
  borderRadius: RADII.pill,
  color: '#6B6458',
  border: '1px solid #D8D0C2',
  background: '#EFE9DD',
});

const SoldOutName = styled('div')({
  fontFamily: FONTS.serif,
  fontSize: '26px',
  fontWeight: 400,
  color: COLORS.ink,
  lineHeight: 1.05,
  letterSpacing: '-0.01em',
});

const SoldOutSeries = styled('div')({
  fontFamily: FONTS.mono,
  fontSize: '11px',
  color: COLORS.ink2,
  marginTop: '9px',
  letterSpacing: '0.03em',
});

const SoldOutDisc = styled('div')({
  fontFamily: FONTS.sans,
  fontSize: '9.5px',
  color: COLORS.ink3,
  lineHeight: 1.5,
  marginTop: '12px',
  textAlign: 'left',
  textWrap: 'pretty',
  opacity: 0.85,
  '& a': {
    color: COLORS.ink3,
    textDecoration: 'underline',
    textDecorationColor: COLORS.edge,
    textUnderlineOffset: '2px',
  },
});

const SoldOutBody = styled('div')({
  padding: '18px 24px 22px',
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
});

const SoldOutMeta = styled('span')({
  alignSelf: 'flex-start',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '7px',
  fontFamily: FONTS.sans,
  fontSize: '12px',
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  marginBottom: '12px',
  padding: '5px 11px 5px 9px',
  borderRadius: RADII.pill,
  color: '#6B6458',
  border: '1px solid #D8D0C2',
  background: '#EFE9DD',
  '&::before': {
    content: '""',
    width: '5px',
    height: '5px',
    borderRadius: '50%',
    background: '#9A9382',
    flexShrink: 0,
  },
});

const SoldOutDesc = styled('p')({
  fontFamily: FONTS.sans,
  fontSize: '16.5px',
  color: COLORS.ink2,
  lineHeight: 1.6,
  marginBottom: '16px',
  textWrap: 'pretty',
  display: '-webkit-box',
  WebkitLineClamp: 6,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  // Reserve a fixed 6-line height so every sold-out card is the same total
  // height regardless of description length — the head is already locked, so
  // this equalizes the full card across the rail (Hannah QA). 16.5px * 1.6 * 6.
  height: 'calc(16.5px * 1.6 * 6)',
});

const SoldOutFigs = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  marginBottom: '12px',
});

const FigLabel = styled('div')({
  fontFamily: FONTS.sans,
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: COLORS.gold,
  marginBottom: '6px',
});

const FigValue = styled('div', {
  shouldForwardProp: (prop) => prop !== 'accent',
})<{ accent?: boolean }>(({ accent }) => ({
  fontFamily: FONTS.serif,
  fontSize: '22px',
  fontWeight: 400,
  lineHeight: 1,
  color: accent ? COLORS.tealInk : COLORS.ink,
}));

const SoldOutTrack = styled('div')({
  height: '4px',
  background: COLORS.edge,
  borderRadius: '90px',
  overflow: 'hidden',
  marginBottom: '18px',
});

const SoldOutFill = styled('div')({
  height: '100%',
  borderRadius: '90px',
  width: '100%',
  background: '#B8AE9E',
});

const SoldOutCta = styled('button')({
  width: '100%',
  fontFamily: FONTS.sans,
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: '#6B6458',
  background: 'transparent',
  border: '1px solid #D8D0C2',
  padding: '12px',
  borderRadius: RADII.md,
  cursor: 'pointer',
  transition: 'background 0.15s, color 0.15s',
  '&:hover': { background: '#EFE9DD', color: COLORS.ink2 },
});

function soldOutDisc(company: Company): string {
  const series = privateSeriesDisplay(company.series);
  const seriesPhrase = series
    ? `${series}, a series of StartEngine Private LLC`
    : 'a series of StartEngine Private LLC';
  return `This offering was for membership interest in ${seriesPhrase}, which held equity of ${company.name}. Investors did not buy ${company.name} shares directly.`;
}

function OfferingCard({
  card,
  company,
  useCompanyLegalName = false,
  variant,
  soldOut,
  disclosure,
  seriesSubhead,
  ctaLabel = 'View details',
  descriptionClampLines,
  fluid = false,
  logoBasePath = PUBLIC_LOGO_BASE,
  onView,
  onCardActivate,
  onTrack,
  cardRef,
  className,
  'data-cy': dataCy,
}: OfferingCardProps) {
  const qaSearch = useSe3QaSearch();
  const tombstoneVariant = useAssignedHomepageTombstoneVariant();
  const resolvedCompany = company ?? (card ? COMPANIES[card.co] : undefined);
  const effectiveLogoBasePath = shouldUseLogoBasePath(resolvedCompany?.logo)
    ? logoBasePath
    : undefined;

  if (card?.locked && resolvedCompany) {
    const activateRestricted = onCardActivate ?? onView;
    return (
      <RestrictedCard
        ref={cardRef}
        fluid={fluid}
        className={className}
        data-cy={dataCy ?? `offering-card-${card.co}`}
        data-restricted="locked"
        role="button"
        tabIndex={0}
        onClickCapture={onTrack}
        onClick={(event) => activateRestricted?.(event.currentTarget)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onTrack?.();
            activateRestricted?.(event.currentTarget);
          }
        }}
        aria-label="Restricted offering. Learn about eligibility."
      >
        <RestrictedFrostedHead aria-hidden data-cy="restricted-card-frosted-head">
          <RestrictedBadge>Limited access</RestrictedBadge>
          <RestrictedSkeleton>
            <span />
            <span />
            <span />
          </RestrictedSkeleton>
        </RestrictedFrostedHead>
        <RestrictedBody data-cy="restricted-card-body">
          <RestrictedIcon aria-hidden>
            <RestrictedLockIcon />
          </RestrictedIcon>
          <RestrictedTitle>Restricted Offering</RestrictedTitle>
          <RestrictedHint>Private opportunity available to eligible investors.</RestrictedHint>
          <RestrictedCta>
            <RestrictedLockIcon /> Learn about access →
          </RestrictedCta>
        </RestrictedBody>
      </RestrictedCard>
    );
  }

  if (soldOut && resolvedCompany) {
    const c = resolvedCompany;
    const rawHref = offeringHref(c);
    const cHref = rawHref ? withSe3QaOverride(rawHref, qaSearch) : null;
    const isCrowd = Boolean(c.regcf || c.regA);
    const isPrivate = !isCrowd;
    const regulationTag = regulationSubhead(c, isCrowd);
    const normalizeSoldOutFigure = (figure: SoldOutCardContent['figR']) => {
      if (!figure) return undefined;
      const label = figure.lbl.trim().toLowerCase();
      if (isPrivate && ['raised', 'invested', 'amount raised', 'amount invested'].includes(label)) {
        return undefined;
      }
      return {
        ...figure,
        lbl: isPrivate && label === 'offering amount' ? 'Funding goal' : figure.lbl,
      };
    };
    const soldOutFigL = normalizeSoldOutFigure(soldOut.figL);
    const soldOutFigR = normalizeSoldOutFigure(soldOut.figR);
    return (
      <SoldOutCard
        ref={cardRef}
        fluid={fluid}
        className={className}
        data-cy={dataCy ?? `offering-card-${c.key}`}
        onClickCapture={onTrack}
      >
        <SoldOutHead>
          <SoldOutBadge data-cy="offering-card-soldout-badge">Sold Out</SoldOutBadge>
          <div style={{ margin: '6px 0 16px', opacity: 0.82 }}>
            <CompanyLogo company={c} size={56} radius={14} logoBasePath={effectiveLogoBasePath} />
          </div>
          <SoldOutName>{c.regA || c.regcf ? c.name : privateCardTitle(c.name)}</SoldOutName>
          {regulationTag && <SoldOutSeries>{regulationTag}</SoldOutSeries>}
          <SoldOutDisc>
            {disclosure ?? soldOutDisc(c)}
            {cHref ? (
              <>
                {' '}
                <a href={cHref}>See full details in footnote 1.</a>
              </>
            ) : null}
          </SoldOutDisc>
        </SoldOutHead>
        <SoldOutBody>
          {soldOut.meta && <SoldOutMeta>{soldOut.meta}</SoldOutMeta>}
          <SoldOutDesc>{card?.desc ?? c.desc}</SoldOutDesc>
          {(soldOutFigL || soldOutFigR) && (
            <SoldOutFigs>
              {soldOutFigL && (
                <div>
                  <FigLabel>{soldOutFigL.lbl}</FigLabel>
                  <FigValue>{compactDollarValue(soldOutFigL.val)}</FigValue>
                </div>
              )}
              {soldOutFigR && (
                <div style={{ textAlign: 'right' }}>
                  <FigLabel>{soldOutFigR.lbl}</FigLabel>
                  <FigValue accent={soldOutFigR.public}>
                    {compactDollarValue(soldOutFigR.val)}
                  </FigValue>
                </div>
              )}
            </SoldOutFigs>
          )}
          {isCrowd && (
            <SoldOutTrack>
              <SoldOutFill />
            </SoldOutTrack>
          )}
          <SoldOutCta
            type="button"
            onClick={(event) => onView?.(event.currentTarget)}
            data-cy="offering-card-cta"
          >
            View Recap
          </SoldOutCta>
        </SoldOutBody>
      </SoldOutCard>
    );
  }

  if (!card || !resolvedCompany) return null;

  const c = resolvedCompany;
  const rawHref = offeringHref(c);
  const cHref = rawHref ? withSe3QaOverride(rawHref, qaSearch) : null;
  const dark = variant === 'dark';
  const isCrowd = Boolean(c.regcf || c.regA);
  const isPrivate = Boolean(c.regD || (!isCrowd && (isRegD(c.reg) || /accredited/i.test(c.reg))));
  const regulationTag = seriesSubhead ?? regulationSubhead(c, isCrowd);
  const intermediaryLabel = regulationIntermediary(c, useCompanyLegalName);
  // Banner-photo treatment: any company explicitly flagged `banner`, plus the
  // existing Reg CF/A+ logo-photo cards. (Behaviour-preserving — the only
  // `banner: true` companies today are Reg CF/A+, so this only newly enables
  // the banner for opt-in non-reg cards like Vino Vest.)
  const hasBanner = Boolean(c.banner) || (isCrowd && Boolean(c.logo));
  // Days remaining is an image-corner badge, never a lower statistic. Filter
  // legacy adapter payloads here as a final safeguard so every tombstone keeps
  // the consistent Invested + availability/minimum layout.
  const rawFigures = [card.figL, card.figR]
    .filter(
      (figure): figure is CardFigure =>
        figure !== undefined &&
        Boolean(figure.val) &&
        figure.lbl.trim().toLowerCase() !== 'days left' &&
        !(
          isPrivate &&
          ['raised', 'invested', 'amount raised', 'amount invested'].includes(
            figure.lbl.trim().toLowerCase(),
          )
        ),
    )
    .map((figure) => ({
      ...figure,
      lbl:
        isPrivate && figure.lbl.trim().toLowerCase() === 'offering amount'
          ? 'Funding goal'
          : figure.lbl,
      val: compactDollarValue(figure.val),
    }));
  const investedFigure = rawFigures.find(
    (figure) => figure.lbl.trim().toLowerCase() === 'invested',
  );
  const fundingGoalFigure = rawFigures.find((figure) => {
    const label = figure.lbl.trim().toLowerCase();
    return label === 'funding goal' || label === 'offering amount';
  });
  const availabilityFigure = rawFigures.find(
    (figure) => figure.lbl.trim().toLowerCase() === 'availability',
  );
  const minimumFigure = rawFigures.find((figure) => {
    const label = figure.lbl.trim().toLowerCase();
    return label === 'minimum' || label === 'min. investment';
  });
  const privateFigures = [
    minimumFigure,
    fundingGoalFigure,
    ...rawFigures.filter((figure) => figure !== fundingGoalFigure && figure !== minimumFigure),
  ].filter((figure): figure is CardFigure => figure !== undefined);
  const preferredRightFigure = rawFigures.find((figure) => {
    const label = figure.lbl.trim().toLowerCase();
    return (
      label === 'availability' ||
      label === 'minimum' ||
      label === 'min. investment' ||
      label === 'funding goal' ||
      label === 'offering amount'
    );
  });
  const rightCandidates = [
    preferredRightFigure,
    ...rawFigures.filter((figure) => figure !== investedFigure && figure !== preferredRightFigure),
  ].filter((figure): figure is CardFigure => figure !== undefined);
  const secondaryLeftFigure = rawFigures.find(
    (figure) => figure !== investedFigure && figure !== preferredRightFigure,
  );
  const leftFigure = isPrivate
    ? privateFigures[0]
    : (minimumFigure ??
      investedFigure ??
      (c.invested
        ? { lbl: 'Invested', val: compactDollarValue(c.invested) }
        : secondaryLeftFigure));
  const publicRightFigure =
    availabilityFigure ??
    (investedFigure !== leftFigure ? investedFigure : undefined) ??
    fundingGoalFigure ??
    rightCandidates.find((figure) => figure !== leftFigure);
  const figL = isPrivate ? (leftFigure ?? rightCandidates[0]) : leftFigure;
  let figR = rightCandidates[0];
  if (isPrivate) {
    figR = privateFigures[1];
  } else if (leftFigure) {
    figR = publicRightFigure ?? { lbl: 'Availability', val: 'Open', cls: 'ok' };
  }
  const isRegACard = Boolean(c.regA || isRegA(c.reg) || isRegA(c.series));
  const fill =
    card.fill && isCrowd
      ? { ...card.fill, cls: isRegACard ? ('amber' as const) : ('urg' as const) }
      : undefined;
  const badge: OfferingCardData['badge'] =
    card.badge ??
    (isCrowd && figR?.lbl === 'Availability' && figR.val === 'Waitlist'
      ? { cls: 'bdg-wine' as const, label: 'Waitlist' }
      : undefined);
  const marketplaceMinimum = rawFigures.find((figure) => {
    const label = figure.lbl.trim().toLowerCase();
    return label === 'minimum' || label === 'min. investment';
  });
  let marketplaceStats: Array<{ label: string; value: string }> = [];
  if (isCrowd) {
    marketplaceStats = [
      investedFigure ? { label: 'Raised', value: investedFigure.val } : null,
      card.numberOfInvestors
        ? {
            label: 'Investors',
            value: new Intl.NumberFormat('en-US').format(card.numberOfInvestors),
          }
        : null,
      marketplaceMinimum ? { label: 'Minimum', value: marketplaceMinimum.val } : null,
    ].filter((stat): stat is { label: string; value: string } => stat !== null);
  } else if (isPrivate) {
    marketplaceStats = privateFigures.map((figure) => ({
      label: figure.lbl,
      value: figure.val,
    }));
  } else if (investedFigure || c.invested) {
    marketplaceStats = [{ label: 'Raised', value: investedFigure?.val ?? c.invested }];
  }
  const cardMetaKind = metaVariant(card.meta);
  const cardClasses = ['tc', card.ft ? 'ft' : '', hasBanner ? 'has-banner' : '', className ?? '']
    .filter(Boolean)
    .join(' ');

  // PD-36055 (Josh): the whole tombstone is clickable, not just the CTA button.
  // Preserve standard link affordances for cards that have a real offering URL:
  // Ctrl/Cmd-click and middle-click open the destination in a background tab.
  // The card remains a div because it contains its own disclosure/CTA links.
  const openInNewTab = () => {
    if (cHref && typeof window !== 'undefined') {
      window.open(cHref, '_blank', 'noopener,noreferrer');
    }
  };
  const activateCard = onCardActivate ?? onView;
  const cardActivation = activateCard
    ? {
        role: 'link' as const,
        tabIndex: 0,
        onClick: (e: MouseEvent<HTMLDivElement>) => {
          if ((e.metaKey || e.ctrlKey) && cHref) {
            openInNewTab();
            return;
          }
          activateCard(e.currentTarget);
        },
        onAuxClick: (e: MouseEvent<HTMLDivElement>) => {
          if (e.button === 1 && cHref) {
            onTrack?.();
            openInNewTab();
          }
        },
        onKeyDown: (e: KeyboardEvent) => {
          // Only act on the card itself — let the CTA button and disclosure
          // link handle their own keyboard activation (don't hijack bubbled
          // Enter/Space from nested interactive controls).
          if (e.currentTarget !== e.target) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onTrack?.();
            activateCard(e.currentTarget as HTMLElement);
          }
        },
      }
    : {};

  const privateExperimentSeries = privateSeriesDisplay(c.series);
  const experimentTitle =
    isCrowd || !privateExperimentSeries || isRegD(privateExperimentSeries)
      ? c.name
      : privateExperimentSeries;
  const experimentDisclosure = disclosure ?? liveDisc(c, useCompanyLegalName);
  const tombstoneEntity = tombstoneIntermediary(c, useCompanyLegalName);
  const experimentRegulationLabel = isCrowd
    ? `${isRegA(c.reg) ? 'Reg A+' : 'Reg CF'} via ${tombstoneEntity}`
    : 'Reg D via StartEngine Primary, LLC';
  let experimentLogoSrc: string | null = null;
  if (c.bannerLogo) {
    experimentLogoSrc = withSe3AssetPrefix(c.bannerLogo) ?? c.bannerLogo;
  } else if (c.logo) {
    experimentLogoSrc = shouldUseLogoBasePath(c.logo)
      ? `${logoBasePath.replace(/\/$/, '')}/${c.logo.replace(/^\//, '')}`
      : c.logo;
  }
  let experimentTombstoneSrc = c.tombstoneImage ?? null;
  if (experimentTombstoneSrc && shouldUseLogoBasePath(experimentTombstoneSrc)) {
    experimentTombstoneSrc = `${logoBasePath.replace(/\/$/, '')}/${experimentTombstoneSrc.replace(/^\//, '')}`;
  }
  let experimentMedia: ReactNode;
  if (hasBanner) {
    experimentMedia = (
      <CompanyLogo
        company={c}
        banner
        logoBasePath={effectiveLogoBasePath}
        data-cy="offering-card-banner"
      />
    );
  } else if (!isCrowd && experimentTombstoneSrc) {
    experimentMedia = (
      <ExperimentTileImage
        src={experimentTombstoneSrc}
        alt={`${c.name} offering`}
        data-cy="offering-card-experiment-tombstone-image"
      />
    );
  } else if (!isCrowd && experimentLogoSrc && !c.bannerLogo && !c.tileBg) {
    experimentMedia = (
      <ExperimentTileImage
        src={experimentLogoSrc}
        alt={`${c.name} logo`}
        data-cy="offering-card-experiment-profile-tile"
      />
    );
  } else {
    experimentMedia = (
      <div className="experiment-logo">
        {experimentLogoSrc ? (
          <ExperimentLogoImage src={experimentLogoSrc} alt={`${c.name} logo`} />
        ) : (
          <span data-cy="offering-card-experiment-wordmark">{c.name}</span>
        )}
      </div>
    );
  }
  const experimentTile = (
    <ExperimentTile
      className="experiment-tile"
      style={c.tileBg ? { background: c.tileBg } : undefined}
      data-cy="offering-card-experiment-tile"
    >
      {experimentMedia}
      {badge && (
        <LiveBadge
          className={`tc-badge ${badge.cls}`}
          tone={badge.cls}
          hasBanner
          data-cy="offering-card-badge"
        >
          {badge.dot && <span className="dot" />}
          {badge.label}
        </LiveBadge>
      )}
      {!isCrowd && (
        <ExperimentDisclosure data-cy="offering-card-overlay-disclosure">
          {experimentDisclosure}
          {!disclosure && cHref && (
            <>
              {' '}
              <a href={cHref} onClick={(e) => e.stopPropagation()}>
                See full details in footnote 1.
              </a>
            </>
          )}
        </ExperimentDisclosure>
      )}
    </ExperimentTile>
  );

  if (tombstoneVariant === 'marketplace-overlay') {
    return (
      <MarketplaceCard
        ref={cardRef}
        fluid={fluid}
        className={className}
        data-cy={dataCy ?? `offering-card-${card.co}`}
        data-tombstone-variant="marketplace-overlay"
        onClickCapture={onTrack}
        {...cardActivation}
      >
        {experimentTile}
        <MarketplaceBody>
          <MarketplaceName data-cy="offering-card-marketplace-name">
            {isCrowd ? experimentTitle : privateCardTitle(experimentTitle)}
          </MarketplaceName>
          <MarketplaceDesc data-cy="offering-card-marketplace-description">
            {card.desc || c.desc}
          </MarketplaceDesc>
          <MarketplaceDivider />
          {marketplaceStats.length > 0 && (
            <MarketplaceStats data-cy="offering-card-marketplace-stats">
              {marketplaceStats.map((stat) => (
                <MarketplaceStat key={stat.label}>
                  <MarketplaceStatValue>{stat.value}</MarketplaceStatValue>
                  <MarketplaceStatLabel>{stat.label}</MarketplaceStatLabel>
                </MarketplaceStat>
              ))}
            </MarketplaceStats>
          )}
          <MarketplaceReg>{experimentRegulationLabel}</MarketplaceReg>
        </MarketplaceBody>
      </MarketplaceCard>
    );
  }

  if (tombstoneVariant === 'hybrid') {
    let hybridCta: ReactNode;
    if (onView) {
      hybridCta = (
        <LiveCta
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onView(e.currentTarget);
          }}
          data-cy="offering-card-cta"
        >
          {ctaLabel}
        </LiveCta>
      );
    } else if (cHref) {
      hybridCta = (
        <LiveCtaLink href={cHref} data-cy="offering-card-cta">
          {ctaLabel}
        </LiveCtaLink>
      );
    } else {
      hybridCta = (
        <LiveCta type="button" disabled data-cy="offering-card-cta">
          {ctaLabel}
        </LiveCta>
      );
    }

    return (
      <HybridCard
        ref={cardRef}
        fluid={fluid}
        className={className}
        data-cy={dataCy ?? `offering-card-${card.co}`}
        data-tombstone-variant="hybrid"
        onClickCapture={onTrack}
        {...cardActivation}
      >
        {experimentTile}
        <HybridBody>
          <HybridName data-cy="offering-card-hybrid-name">
            {isCrowd ? experimentTitle : privateCardTitle(experimentTitle)}
          </HybridName>
          <HybridDesc>{card.desc || c.desc}</HybridDesc>
          {(figL || figR) && (
            <LiveFigs className="tc-figs">
              {figL && (
                <div className="tc-fig">
                  <LiveFigLabel>{figL.lbl}</LiveFigLabel>
                  <LiveFigValue tone={figL.cls}>{figL.val}</LiveFigValue>
                </div>
              )}
              {figR && (
                <div className="tc-fig right">
                  <LiveFigLabel>{figR.lbl}</LiveFigLabel>
                  <LiveFigValue tone={figR.cls}>{figR.val}</LiveFigValue>
                </div>
              )}
            </LiveFigs>
          )}
          {fill ? (
            <LiveTrack>
              <LiveFill
                tone={fill.cls}
                style={{ width: `${fill.w ?? 0}%` }}
                data-cy="progress-meter-fill"
              />
            </LiveTrack>
          ) : (
            <LiveTrackSpacer aria-hidden data-cy="progress-meter-spacer" />
          )}
          <HybridReg>{experimentRegulationLabel}</HybridReg>
          {hybridCta}
        </HybridBody>
      </HybridCard>
    );
  }

  return (
    <LiveCard
      ref={cardRef}
      dark={dark}
      fluid={fluid}
      className={cardClasses}
      data-cy={dataCy ?? `offering-card-${card.co}`}
      data-tombstone-variant="control"
      onClickCapture={onTrack}
      {...cardActivation}
    >
      {hasBanner && (
        <LiveBanner className="tc-banner">
          <CompanyLogo
            company={c}
            banner
            logoBasePath={effectiveLogoBasePath}
            data-cy="offering-card-banner"
            className="tc-banner-img"
          />
        </LiveBanner>
      )}
      {badge && (
        <LiveBadge
          className={`tc-badge ${badge.cls}`}
          tone={badge.cls}
          hasBanner={hasBanner}
          data-cy="offering-card-badge"
        >
          {badge.dot && <span className="dot" />}
          {badge.label}
        </LiveBadge>
      )}
      <LiveHead className="tc-head" dark={dark} hasBanner={hasBanner}>
        {!hasBanner && (
          <LogoMark>
            <CompanyLogo company={c} size={56} radius={14} logoBasePath={effectiveLogoBasePath} />
          </LogoMark>
        )}
        <LiveName className="tc-name" dark={dark}>
          {isCrowd ? c.name : privateCardTitle(c.name, dark)}
        </LiveName>
        {regulationTag && (
          <LiveSeries className="tc-series" dark={dark}>
            {regulationTag}
          </LiveSeries>
        )}
        <LiveDisc className="tc-disc" dark={dark}>
          {disclosure ?? liveDisc(c, useCompanyLegalName)}
          {!disclosure && shouldShowDisclosureLink(c) && cHref && (
            <>
              {' '}
              <a href={cHref} onClick={(e) => e.stopPropagation()}>
                See full details in footnote 1.
              </a>
            </>
          )}
        </LiveDisc>
      </LiveHead>
      <LiveBody className="tc-body">
        <LiveMetaSlot className="tc-meta-slot" data-cy="offering-card-meta-slot">
          {card.meta && (
            <LiveMeta
              className={cardMetaKind ? `tc-meta-pill mp-${cardMetaKind}` : 'tc-meta-pill'}
              kind={cardMetaKind}
              dark={dark}
            >
              {card.meta}
            </LiveMeta>
          )}
        </LiveMetaSlot>
        <LiveDesc
          className="tc-desc"
          dark={dark}
          clampLines={descriptionClampLines}
          data-cy="offering-card-description"
          data-clamp-lines={descriptionClampLines}
        >
          {card.desc || c.desc}
        </LiveDesc>
        {(figL || figR) && (
          <LiveFigs className="tc-figs">
            {figL && (
              <div className="tc-fig">
                <LiveFigLabel className="tc-fig-l">{figL.lbl}</LiveFigLabel>
                <LiveFigValue
                  className={figL.cls ? `tc-fig-n ${figL.cls}` : 'tc-fig-n'}
                  tone={figL.cls}
                  dark={dark}
                >
                  {figL.val}
                </LiveFigValue>
              </div>
            )}
            {figR && (
              <div className="tc-fig right">
                <LiveFigLabel className="tc-fig-l">{figR.lbl}</LiveFigLabel>
                <LiveFigValue
                  className={figR.cls ? `tc-fig-n ${figR.cls}` : 'tc-fig-n'}
                  tone={figR.cls}
                  dark={dark}
                >
                  {figR.val}
                </LiveFigValue>
              </div>
            )}
          </LiveFigs>
        )}
        {fill ? (
          <LiveTrack className="tc-track">
            <LiveFill
              className={fill.cls ? `tc-fill ${fill.cls}` : 'tc-fill'}
              tone={fill.cls}
              style={{ width: `${fill.w ?? 0}%` }}
              data-cy="progress-meter-fill"
            />
          </LiveTrack>
        ) : (
          <LiveTrackSpacer
            aria-hidden
            className="tc-track-spacer"
            data-cy="progress-meter-spacer"
          />
        )}
        {intermediaryLabel && (
          <LiveIntermediary
            className="tc-intermediary"
            dark={dark}
            data-cy="offering-card-intermediary"
          >
            {intermediaryLabel}
          </LiveIntermediary>
        )}
        {onView ? (
          <LiveCta
            className="tc-btn"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onView?.(e.currentTarget);
            }}
            data-cy="offering-card-cta"
            dark={dark}
          >
            {ctaLabel}
          </LiveCta>
        ) : (
          <>
            {cHref ? (
              <LiveCtaLink className="tc-btn" href={cHref} data-cy="offering-card-cta" dark={dark}>
                {ctaLabel}
              </LiveCtaLink>
            ) : (
              <LiveCta
                className="tc-btn"
                type="button"
                data-cy="offering-card-cta"
                dark={dark}
                disabled
              >
                {ctaLabel}
              </LiveCta>
            )}
          </>
        )}
      </LiveBody>
    </LiveCard>
  );
}

export default OfferingCard;
